import type { ReactNode } from 'react';
import styles from './Table.module.css';

interface Column<T> {
    key: keyof T | string;
    header: string;
    render?: (item: T) => ReactNode;
    sortable?: boolean;
}

export interface TableSort {
    key: string;
    direcao: 'asc' | 'desc';
}

interface TableProps<T> {
    columns: Column<T>[];
    data: T[];
    keyExtractor: (item: T) => string;
    isLoading?: boolean;
    /** Coluna ordenada agora. Com `onSort`, o cabecalho das colunas `sortable` vira clicavel. */
    sort?: TableSort;
    onSort?: (key: string) => void;
    emptyState?: {
        icon?: ReactNode;
        title: string;
        description?: string;
    };
}

export function Table<T>({
    columns,
    data,
    keyExtractor,
    isLoading = false,
    emptyState,
    sort,
    onSort,
}: TableProps<T>) {
    if (isLoading) {
        return (
            <div className={styles.loading}>
                Carregando...
            </div>
        );
    }

    if (data.length === 0 && emptyState) {
        return (
            <div className={styles.emptyState}>
                {emptyState.icon && (
                    <div className={styles.emptyIcon}>{emptyState.icon}</div>
                )}
                <h3 className={styles.emptyTitle}>{emptyState.title}</h3>
                {emptyState.description && (
                    <p className={styles.emptyDescription}>{emptyState.description}</p>
                )}
            </div>
        );
    }

    return (
        <table className={styles.table}>
            <thead className={styles.thead}>
                <tr>
                    {columns.map((column) => {
                        const key = String(column.key);
                        const clicavel = column.sortable && !!onSort;
                        const ativa = clicavel && sort?.key === key;
                        return (
                            <th
                                key={key}
                                className={`${styles.th} ${column.sortable ? styles.sortable : ''} ${ativa ? styles.sortActive : ''}`}
                                onClick={clicavel ? () => onSort(key) : undefined}
                                aria-sort={ativa ? (sort!.direcao === 'asc' ? 'ascending' : 'descending') : undefined}
                            >
                                {column.header}
                                {column.sortable && (
                                    <span className={styles.sortIcon}>
                                        {ativa ? (sort!.direcao === 'asc' ? '↑' : '↓') : '↕'}
                                    </span>
                                )}
                            </th>
                        );
                    })}
                </tr>
            </thead>
            <tbody className={styles.tbody}>
                {data.map((item) => (
                    <tr key={keyExtractor?.(item)}>
                        {columns.map((column) => (
                            <td key={String(column.key)} className={styles.td}>
                                {column.render
                                    ? column.render(item)
                                    : String(item[column.key as keyof T] ?? '')}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
